/**
 * content-builder-neo.js
 *
 * Portable renderer for the AICO Neo content builder (`contentBuilderNeoJson`).
 * Self-contained: no jQuery, no framework — it reads a JSON blob, emits HTML
 * for each section/element type (heading, text, image, gallery, media-gallery,
 * slideshow, video, youtube, button, table, file-list, badge, spacer, faq, testimonial,
 * columns…), injects its own component CSS, and
 * hydrates sliders / lightboxes.
 *
 * Drop-in usage in ANY theme:
 *   1. <div id="aico-content"></div>                                  (mount)
 *   2. <script id="aico-page-data" type="application/json">…</script> (data)
 *      where the JSON is shaped `{"contentBuilderNeoJson": {sections:[…]}}`
 *   3. include this file (defer). The bootstrap at the bottom finds the mount +
 *      data and paints automatically. To render elsewhere, call
 *      window.NewsNeo.render(data) / .hydrate(mount) yourself.
 *
 * Public API (window.NewsNeo — name kept for parity with the source renderer):
 *   - hasContent(data)       → boolean
 *   - render(data)           → HTML string
 *   - hydrate(root)          → wire sliders/lightboxes inside root
 *   - registerType(type, fn) → add or override an element renderer
 *
 * Ported verbatim from the Shopify news-neo.js; only this header and the
 * bootstrap IIFE at the end of the file are AICO-theme additions.
 */
(function (window) {
  'use strict';

  // Forward styles.desktop from the JSON to the DOM
  var USE_API_INLINE_STYLES = true;

  // ============================================================
  // Detection
  // ============================================================

  function hasNeoContent(data) {
    if (!data || typeof data !== 'object') return false;
    var neo = data.contentBuilderNeoJson;
    if (!neo || typeof neo !== 'object') return false;
    if (!Array.isArray(neo.sections) || neo.sections.length === 0) return false;
    return true;
  }

  // ============================================================
  // Style helpers (used only when USE_API_INLINE_STYLES is true)
  // ============================================================

  function camelToKebab(s) {
    return s.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  // Expand `padding`/`margin` shorthand into longhands when both are present, so longhands always win.
  function normalizeShorthands(styleObj) {
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k)) out[k] = styleObj[k];
    }
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    ['padding', 'margin'].forEach(function (base) {
      var hasIndividual = sides.some(function (s) { return (base + s) in out; });
      if (!hasIndividual || !(base in out)) return;
      var parts = String(out[base]).trim().split(/\s+/);
      var top = parts[0];
      var right = parts[1] !== undefined ? parts[1] : top;
      var bottom = parts[2] !== undefined ? parts[2] : top;
      var left = parts[3] !== undefined ? parts[3] : right;
      var fill = { Top: top, Right: right, Bottom: bottom, Left: left };
      sides.forEach(function (s) { if (!(base + s in out)) out[base + s] = fill[s]; });
      delete out[base];
    });
    return out;
  }

  function stylesToInline(styleObj) {
    if (!styleObj || typeof styleObj !== 'object' || Array.isArray(styleObj)) return '';
    var normalized = normalizeShorthands(styleObj);
    var out = [];
    for (var key in normalized) {
      if (!Object.prototype.hasOwnProperty.call(normalized, key)) continue;
      var val = normalized[key];
      if (val === null || val === undefined || val === '') continue;
      out.push(camelToKebab(key) + ':' + val);
    }
    return out.join(';');
  }

  function pickStyles(node, breakpoint) {
    if (!node || !node.styles) return {};
    var s = node.styles[breakpoint || 'desktop'];
    if (!s || Array.isArray(s)) return {};
    return s;
  }

  function inlineStyleAttr(node) {
    if (!USE_API_INLINE_STYLES) return '';
    var s = stylesToInline(pickStyles(node));
    return s ? ' style="' + s + '"' : '';
  }

  // Strip a single wrapping <p> so heading text from the rich-text editor isn't invalid <hN><p>.
  function stripOuterParagraph(html) {
    if (!html) return '';
    return String(html).replace(/^\s*<p[^>]*>([\s\S]*?)<\/p>\s*$/i, '$1');
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Pick the active translation: window.current_locale_code if matched, else first available.
  function pickTranslation(translations) {
    if (!translations || typeof translations !== 'object') return {};
    var loc = (typeof window !== 'undefined' && window.current_locale_code) ? window.current_locale_code : null;
    if (loc && translations[loc]) return translations[loc];
    for (var k in translations) {
      if (Object.prototype.hasOwnProperty.call(translations, k)) return translations[k] || {};
    }
    return {};
  }

  // Prefix bare domains with https:// so they don't get treated as relative paths.
  function normalizeUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (!s) return '';
    if (/^javascript:/i.test(s)) return '';
    if (/^([a-z][a-z0-9+.\-]*:|\/\/|\/|#)/i.test(s)) return s;
    if (/^[^\s\/?]+\.[a-z]{2,}/i.test(s)) return 'https://' + s;
    return s;
  }

  // Element renderers return inner HTML only; row/column wrapping lives in
  // renderSection. The `columns` type is handled there, not here.
  var renderers = {};

  renderers.heading = function (el) {
    var content = el.content || {};
    var level = content.level || 'h2';
    if (!/^h[1-6]$/i.test(level)) level = 'h2';
    var text = stripOuterParagraph(content.text != null ? content.text : '');
    return '<div class="content-type-title content-type-neo-heading">' +
      '<' + level + inlineStyleAttr(el) + '>' + text + '</' + level + '>' +
      '</div>';
  };

  renderers.text = function (el) {
    var content = el.content || {};
    var text = content.text != null ? content.text : '';
    return '<div class="content-type-text content-type-neo-text"' + inlineStyleAttr(el) + '>' + text + '</div>';
  };

  // Custom HTML block — render the author's raw markup verbatim (matches the React builder's
  // `html` element, which stores HTML untouched by the rich-text engine).
  renderers.html = function (el) {
    var content = el.content || {};
    var html = content.html != null ? content.html : '';
    return '<div class="content-type-html content-type-neo-html"' + inlineStyleAttr(el) + '>' + html + '</div>';
  };

  // What each captionStyle an image element may carry declares on its caption. Read off the view
  // the migrated pages were drawn by (core::shopify-pages.content-text), whose trailing <style>
  // states the four of them: .picture-label-H1{font-size:22px!important;font-weight:bold} and its
  // H2/H3/italic siblings. The same view writes color:black;line-height:1.3;font-size:14px inline
  // on the container as the base every caption gets, so 'none' (the picker's `p`) declares nothing:
  // 14px IS the unstyled caption. Mirrors NeoLandingPageBodyRenderer::CAPTION_STYLES, so a page
  // drawn here and the same page drawn by the server agree about the caption.
  var CAPTION_STYLES = {
    h1: 'font-size:22px;font-weight:700',
    h2: 'font-size:18px;font-weight:700',
    h3: 'font-size:16px;font-weight:700',
    italic: 'font-size:16px;font-style:italic',
    none: '',
  };

  var CAPTION_ALIGNMENTS = ['left', 'center', 'right'];

  // The author's caption look as an inline style, or '' when they chose neither a variant nor an
  // alignment — in which case the caption renders exactly as every caption stored before this
  // existed. No !important: the legacy flag was there to beat the view's own inline 14px, and here
  // the declaration already IS the inline style.
  function captionStyleAttr(content) {
    var declarations = CAPTION_STYLES[content.captionStyle] || '';
    var align = content.captionAlign;
    if (CAPTION_ALIGNMENTS.indexOf(align) !== -1) {
      declarations += (declarations ? ';' : '') + 'text-align:' + align;
    }
    return declarations ? ' style="' + escapeAttr(declarations) + '"' : '';
  }

  // The picture's own style attribute: the frame a zoomed picture crops into, then the element's
  // own geometry. object-fit is inert without a box, so the flag brings one with it — the frame the
  // legacy card gave the same picture, .aico-page-link-image{height:300px} with
  // .aico-page-link-image img{height:100%;width:100%}, where `zoomed` is the single declaration
  // that changed, contain to cover. The element's own styles come after and therefore win.
  // Mirrors NeoLandingPageBodyRenderer::imageGeometryAttribute().
  function imageStyleAttr(node, content) {
    var declarations = [];
    if (content.zoomed === true) {
      declarations.push('width:100%', 'height:300px', 'object-fit:cover');
    }
    if (USE_API_INLINE_STYLES) {
      var own = stylesToInline(pickStyles(node));
      if (own) declarations.push(own);
    }
    return declarations.length ? ' style="' + escapeAttr(declarations.join(';')) + '"' : '';
  }

  renderers.image = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    if (!src) return '';
    var alt = escapeAttr(content.alt || '');
    var caption = content.caption || '';
    // Inline styles go on the <img> — no legacy .image-container wrapper (forced max-width:75%).
    var html = '<div class="content-type-neo-image">';
    html += '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy"' + imageStyleAttr(el, content) + '>';
    if (caption) {
      // Escaped, because the server renderer escapes it (`e($caption)`) and the two have to agree
      // about the SAME string: picture_label is author-controlled, and a label holding markup drew
      // as text on the static body and executed as markup here.
      html += '<span class="caption"' + captionStyleAttr(content) + '>' + escapeAttr(caption) + '</span>';
    }
    html += '</div>';
    return html;
  };

  renderers.divider = function (el) {
    return '<div class="content-type-divider content-type-neo-divider"><hr' + inlineStyleAttr(el) + '></div>';
  };

  // ----- design tokens (builder look) -----
  //
  // The builder styles buttons through page-level design tokens
  // (designTokens.components.buttonPrimary → semantic colors → color tokens),
  // not through per-element styles — the stored element is usually a bare
  // `{type:'button', content:{text,url}}`. Resolve the same chain here so the
  // storefront button matches what the author sees in the builder; the
  // element's own styles.desktop still wins over tokens as the author's
  // explicit override.
  var currentTokens = null;

  function resolveTokenColor(value, tokens) {
    if (!value || typeof value !== 'string') return null;
    if (/^(#|rgb|hsl|var\(|transparent$|currentcolor$)/i.test(value)) return value;
    var colors = (tokens && tokens.colors) || {};
    var semantic = (tokens && tokens.semantic && tokens.semantic.colors) || {};
    var key = semantic[value] || value;
    return colors[key] || null;
  }

  function buttonTokenStyles(tokens) {
    var comp = tokens && tokens.components && tokens.components.buttonPrimary;
    if (!comp || typeof comp !== 'object') return {};
    // State-based shape `{default:{…},radius,padding,fontSize}`; legacy docs
    // stored the default state flat on the component itself.
    var def = (comp['default'] && typeof comp['default'] === 'object') ? comp['default'] : comp;
    var out = {};
    var bg = resolveTokenColor(def.background, tokens);
    var fg = resolveTokenColor(def.color, tokens);
    var border = resolveTokenColor(def.borderColor, tokens);
    if (bg) out.backgroundColor = bg;
    if (fg) out.color = fg;
    if (border) out.border = '1px solid ' + border;
    if (typeof comp.radius === 'string' && comp.radius) out.borderRadius = comp.radius;
    if (typeof comp.padding === 'string' && comp.padding) {
      out.padding = ((tokens.spacing || {})[comp.padding]) || comp.padding;
    }
    if (typeof comp.fontSize === 'string' && comp.fontSize) {
      out.fontSize = ((tokens.typography || {})[comp.fontSize]) || comp.fontSize;
    }
    return out;
  }

  // The builder's `button` element (`content: {text, url}`). Markup matches the
  // legacy link block (`.content-type-link` + `a.button button--primary`); the
  // injected baseline CSS makes it a styled CTA even without tokens, the page's
  // design tokens repaint it to the builder's exact look, and the element's own
  // styles.desktop wins over both.
  renderers.button = function (el) {
    var content = el.content || {};
    var text = content.text != null ? String(content.text) : '';
    var url = normalizeUrl(content.url || '');
    if (!text || !url) return '';
    var merged = buttonTokenStyles(currentTokens);
    // The builder's Style-tab alignment writes `textAlign` on the ELEMENT;
    // on the inline-block <a> it would only center the label, so route it to
    // the wrapper div — that is what actually moves the button (mirrors the
    // builder canvas, where element styles land on the element's wrapper).
    var wrapperStyles = {};
    if (USE_API_INLINE_STYLES) {
      var own = pickStyles(el);
      for (var key in own) {
        if (!Object.prototype.hasOwnProperty.call(own, key)) continue;
        if (key === 'textAlign') { wrapperStyles.textAlign = own[key]; continue; }
        merged[key] = own[key];
      }
    }
    var styleStr = stylesToInline(merged);
    var wrapperStr = stylesToInline(wrapperStyles);
    return '<div class="content-type-link content-type-neo-button"' + (wrapperStr ? ' style="' + wrapperStr + '"' : '') + '>' +
      '<a href="' + escapeAttr(url) + '" class="button button--primary"' + (styleStr ? ' style="' + styleStr + '"' : '') + '>' + text + '</a>' +
      '</div>';
  };

  // ----- video helpers -----

  function youtubeId(url) {
    if (!url) return '';
    var m = String(url).match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : '';
  }

  function vimeoId(url) {
    if (!url) return '';
    var m = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : '';
  }

  function parseAspectRatio(s, fallback) {
    if (!s) return fallback;
    var str = String(s).replace(':', '/');
    if (/^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/.test(str)) return str;
    return fallback;
  }

  renderers.youtube = function (el) {
    var content = el.content || {};
    var id = content.videoId || youtubeId(content.src || '');
    if (!id) return '';
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');
    var title = escapeAttr(content.videoTitle || 'YouTube video');
    var iframe = '<iframe title="' + title + '" src="https://www.youtube.com/embed/' + escapeAttr(id) +
      '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    return '<div class="content-type-neo-youtube"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-video__frame" style="aspect-ratio:' + aspect + ';">' + iframe + '</div>' +
      '</div>';
  };

  renderers.video = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    if (!src) return '';
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');
    var type = content.type || 'custom';
    var inner;
    if (type === 'youtube') {
      var yid = youtubeId(src);
      if (!yid) return '';
      inner = '<iframe src="https://www.youtube.com/embed/' + escapeAttr(yid) +
        '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    } else if (type === 'vimeo') {
      var vid = vimeoId(src);
      if (!vid) return '';
      inner = '<iframe src="https://player.vimeo.com/video/' + escapeAttr(vid) +
        '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    } else {
      inner = '<video src="' + escapeAttr(src) + '" controls></video>';
    }
    return '<div class="content-type-neo-video"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-video__frame" style="aspect-ratio:' + aspect + ';">' + inner + '</div>' +
      '</div>';
  };

  renderers.gallery = function (el) {
    var content = el.content || {};
    var rawImages = Array.isArray(content.images) ? content.images : [];
    var images = [];
    for (var i = 0; i < rawImages.length; i++) {
      if (rawImages[i] && rawImages[i].src) images.push(rawImages[i]);
    }
    if (images.length === 0) return '';

    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;

    var variant = content.layoutVariant
      ? ' content-type-neo-gallery--' + escapeAttr(String(content.layoutVariant))
      : '';

    var apiStyles = USE_API_INLINE_STYLES ? stylesToInline(pickStyles(el)) : '';
    var gridCols = 'grid-template-columns:repeat(' + cols + ',1fr)';
    var combined = apiStyles ? apiStyles + ';' + gridCols : gridCols;

    var html = '<div class="content-type-neo-gallery' + variant + '" style="' + combined + '">';
    for (var k = 0; k < images.length; k++) {
      var img = images[k];
      html += '<img src="' + escapeAttr(img.src) + '" alt="' + escapeAttr(img.alt || '') + '" loading="lazy">';
    }
    html += '</div>';
    return html;
  };

  function renderMediaGalleryItem(file, showCaptions) {
    var src = file.optimizedImageUrl || file.url;
    var t = pickTranslation(file.translations);
    var alt = escapeAttr(t.alternativeText || t.name || '');
    var caption = t.caption || '';
    var html = '<figure class="neo-media-gallery__item">';
    html += '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy">';
    if (showCaptions && caption) {
      html += '<figcaption class="neo-media-gallery__caption">' + caption + '</figcaption>';
    }
    html += '</figure>';
    return html;
  }

  renderers['media-gallery'] = function (el) {
    var content = el.content || {};
    var rawFiles = Array.isArray(content.files) ? content.files : [];
    if (rawFiles.length === 0) return '';

    var files = rawFiles.slice().sort(function (a, b) {
      var ao = (a && typeof a.order === 'number') ? a.order : 0;
      var bo = (b && typeof b.order === 'number') ? b.order : 0;
      return ao - bo;
    }).filter(function (f) {
      return f && (f.url || f.optimizedImageUrl);
    });
    if (files.length === 0) return '';

    // The author's pictures-per-row, with no upper edge of ours. This used to clamp to 6, which
    // drew a strip authored at ten-up as two rows where the page it replaces draws one; the static
    // body renderer dropped the same clamp, and the two have to agree or the same document draws
    // two shapes depending on which renderer got to it.
    //
    // The 100 is a net under a broken document, not a layout opinion. A document can arrive from an
    // import or a hand edit rather than from a picker, and repeat(5000,1fr) would flatten the
    // pictures into invisible slivers. The legacy picker stops at 12, the builder's control is a
    // 1..6 slider and the widest gallery measured is 10, so this cannot fire on anything an editor
    // is able to author.
    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 100) cols = 100;

    var layout = (content.layout || 'grid').toLowerCase();
    if (layout !== 'grid' && layout !== 'masonry' && layout !== 'carousel') layout = 'grid';

    var showCaptions = content.showCaptions !== false;
    var subsectionTitle = content.subsectionTitle || '';
    var variantClass = ' content-type-neo-media-gallery--' + layout;

    var inner = '';
    if (layout === 'masonry') {
      inner += '<div class="neo-media-gallery__masonry" style="column-count:' + cols + ';">';
      for (var i = 0; i < files.length; i++) inner += renderMediaGalleryItem(files[i], showCaptions);
      inner += '</div>';
    } else if (layout === 'carousel') {
      var hasOverflow = files.length > cols;
      inner += '<div class="neo-media-gallery__carousel" data-neo-mg-carousel="1" data-cols="' + cols + '">';
      inner += '<div class="neo-media-gallery__carousel-track" style="--neo-mg-cols:' + cols + ';">';
      for (var j = 0; j < files.length; j++) inner += renderMediaGalleryItem(files[j], showCaptions);
      inner += '</div>';
      if (hasOverflow) {
        inner += '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--prev" aria-label="Previous">&#10094;</button>';
        inner += '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--next" aria-label="Next">&#10095;</button>';
        inner += '<div class="neo-media-gallery__scrubber" style="position:relative;height:6px;background:#d0d0d0;border-radius:3px;margin-top:16px;cursor:pointer;touch-action:none;user-select:none;overflow:hidden;">';
        inner += '<div class="neo-media-gallery__scrubber-thumb" style="position:absolute;top:0;left:0;height:100%;width:60px;min-width:60px;background:#333;border-radius:3px;pointer-events:none;display:block;"></div>';
        inner += '</div>';
      }
      inner += '</div>';
    } else {
      inner += '<div class="neo-media-gallery__grid" style="grid-template-columns:repeat(' + cols + ',1fr);">';
      for (var k = 0; k < files.length; k++) inner += renderMediaGalleryItem(files[k], showCaptions);
      inner += '</div>';
    }

    var html = '<div class="content-type-neo-media-gallery' + variantClass + '"' + inlineStyleAttr(el) + '>';
    if (subsectionTitle) html += '<h3 class="neo-media-gallery__title">' + subsectionTitle + '</h3>';
    html += inner;
    html += '</div>';
    return html;
  };

  renderers.slideshow = function (el) {
    var content = el.content || {};
    var rawSlides = Array.isArray(content.slides) ? content.slides : [];
    var slides = [];
    for (var i = 0; i < rawSlides.length; i++) {
      if (rawSlides[i] && rawSlides[i].src) slides.push(rawSlides[i]);
    }
    if (slides.length === 0) return '';

    var autoplay = content.autoplay === true;
    var interval = parseInt(content.autoplayInterval, 10);
    if (!isFinite(interval) || interval < 1000) interval = 5000;
    var showDots = content.showDots !== false;
    var showArrows = content.showArrows !== false;
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');

    var html = '<div class="content-type-text content-type-neo-slideshow"' +
      ' data-neo-slideshow="1"' +
      ' data-autoplay="' + (autoplay ? '1' : '0') + '"' +
      ' data-interval="' + interval + '"' +
      inlineStyleAttr(el) + '>';
    html += '<div class="neo-slideshow__viewport" style="aspect-ratio:' + aspect + ';">';
    html += '<div class="neo-slideshow__track">';
    for (var s = 0; s < slides.length; s++) {
      var slide = slides[s];
      html += '<div class="neo-slideshow__slide">';
      html += '<img src="' + escapeAttr(slide.src) + '" alt="' + escapeAttr(slide.alt || '') + '" loading="lazy">';
      if (slide.headline || (slide.buttonText && slide.buttonUrl)) {
        html += '<div class="neo-slideshow__caption">';
        if (slide.headline) html += '<h3 class="neo-slideshow__headline" style="color:#fff !important;font-size:28px !important;font-weight:600 !important;line-height:1.2 !important;margin:0 !important;">' + slide.headline + '</h3>';
        var btnUrl = normalizeUrl(slide.buttonUrl);
        if (slide.buttonText && btnUrl) {
          html += '<a class="neo-slideshow__btn" href="' + escapeAttr(btnUrl) + '">' + slide.buttonText + '</a>';
        }
        html += '</div>';
      }
      html += '</div>';
    }
    html += '</div>'; // track
    if (showArrows && slides.length > 1) {
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--prev" aria-label="Previous slide">&#10094;</button>';
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--next" aria-label="Next slide">&#10095;</button>';
    }
    html += '</div>'; // viewport
    if (showDots && slides.length > 1) {
      html += '<div class="neo-slideshow__dots">';
      for (var d = 0; d < slides.length; d++) {
        html += '<button type="button" class="neo-slideshow__dot" data-idx="' + d + '" aria-label="Go to slide ' + (d + 1) + '"></button>';
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  };

  // ============================================================
  // Static-content types the legacy→neo converters emit
  // (table, file-list, badge, spacer, faq, testimonial)
  //
  // Same markup as the PHP NeoLandingPageBodyRenderer (the Shopify news body and
  // the featured-news popup), so one document looks the same on every path. A
  // migrated FC St.Gallen "Trainingsplan" IS a table; before these arms existed
  // the type fell through renderUnknown() and left the article with nothing in
  // its place.
  // ============================================================

  var CSS_LENGTH_RE = /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/;
  var TABLE_STYLE = 'width:100%;border-collapse:collapse;table-layout:auto;font-size:15px;line-height:1.45';
  var TABLE_CELL_STYLE = 'padding:8px 12px;border:1px solid #dcdcdc;vertical-align:top';
  // A fixed light background needs a fixed dark text colour: on a dark theme the inherited body
  // colour is white and the header row reads as an empty bar.
  var TABLE_HEADER_CELL_STYLE = TABLE_CELL_STYLE + ';background-color:#f4f4f5;color:#111;font-weight:700';
  // <b>/<i> are normalised to <strong>/<em>, like the PHP renderer does.
  var TABLE_CELL_INLINE_TAGS = { a: 'a', strong: 'strong', b: 'strong', em: 'em', i: 'em', br: 'br' };

  function escapeHtml(s) {
    return escapeAttr(s);
  }

  // Entries of a list-valued content field that are plain objects (cells, items,
  // columns), tolerating any malformed shape.
  function objectEntries(value) {
    if (!Array.isArray(value)) return [];
    var out = [];
    for (var i = 0; i < value.length; i++) {
      var v = value[i];
      if (v && typeof v === 'object' && !Array.isArray(v)) out.push(v);
    }
    return out;
  }

  // Entries that are themselves lists (table rows).
  function arrayEntries(value) {
    if (!Array.isArray(value)) return [];
    var out = [];
    for (var i = 0; i < value.length; i++) {
      if (Array.isArray(value[i])) out.push(value[i]);
    }
    return out;
  }

  function trimString(v) {
    return v == null ? '' : String(v).replace(/^\s+|\s+$/g, '');
  }

  // Allowlist of inert URL shapes for an href, mirroring the PHP renderer's
  // isSafeUrl(): control characters are stripped first (browsers ignore them when
  // resolving a scheme, so "java\tscript:" would otherwise slip a denylist), then
  // only http(s)/mailto/tel and scheme-less URLs pass.
  function isSafeUrl(url) {
    var normalized = String(url == null ? '' : url).replace(/[\x00-\x20\x7F]+/g, '');
    if (!normalized) return false;
    if (/^(?:https?|mailto|tel):/i.test(normalized)) return true;
    return !/^[^\/?#]*:/.test(normalized);
  }

  // The converter entity-encodes attribute values; decode before validating so the
  // check sees what the browser will resolve, and re-escape on the way out.
  // Text of markup, for the places that can only hold text (an accordion's <summary>). Mirrors the
  // PHP renderer's strip_tags(): tags out, their content kept, entities decoded by the caller.
  function stripTags(v) {
    return String(v == null ? '' : v).replace(/<[^>]*>/g, '');
  }

  function decodeEntities(s) {
    return String(s)
      .replace(/&#x([0-9a-f]+);/gi, function (_, hex) { return String.fromCharCode(parseInt(hex, 16)); })
      .replace(/&#(\d+);/g, function (_, dec) { return String.fromCharCode(parseInt(dec, 10)); })
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  // Rebuild an <a …> opening tag from href + target only, or null when the href is
  // absent or fails the allowlist (the caller then unwraps the anchor so the label
  // still reaches the reader). target="_blank" forces rel="noopener noreferrer".
  function inlineAnchorOpenTag(tagMarkup) {
    var attrs = {};
    var re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g;
    var m;
    while ((m = re.exec(tagMarkup)) !== null) {
      var value = m[2] !== undefined && m[2] !== '' ? m[2] : (m[3] !== undefined && m[3] !== '' ? m[3] : (m[4] || ''));
      attrs[m[1].toLowerCase()] = decodeEntities(value);
    }
    var href = trimString(attrs.href);
    if (!href || !isSafeUrl(href)) return null;
    var target = trimString(attrs.target).toLowerCase() === '_blank'
      ? ' target="_blank" rel="noopener noreferrer"'
      : '';
    return '<a href="' + escapeAttr(href) + '"' + target + '>';
  }

  function closeInlineTag(name, open) {
    var tag = TABLE_CELL_INLINE_TAGS[name];
    if (!tag || tag === 'br') return '';
    var depth = -1;
    for (var i = 0; i < open.length; i++) {
      if (open[i].tag === tag) depth = i;
    }
    if (depth < 0) return '';
    var html = '';
    while (open.length > depth) {
      var entry = open.pop();
      if (entry.emitted) html += '</' + entry.tag + '>';
    }
    return html;
  }

  // A table cell's `text` reduced to <a href target rel>, <strong>, <em>, <br> —
  // everything else unwrapped to its content, <script>/<style>/comments dropped
  // body and all, every attribute dropped except a validated anchor href/target,
  // tags balanced. A sanitiser, not a pass-through: the cell markup is
  // machine-built by the converter from scraped GrapesJS nodes, and this string
  // lands in the page via innerHTML, so it is re-checked at the point of
  // emission. Port of NeoLandingPageBodyRenderer::sanitizeTableCellHtml(); text
  // nodes are not re-escaped (the converter already escaped them), only a stray
  // `<`/`>` that survived the tag split is.
  function sanitizeInlineHtml(html) {
    html = String(html == null ? '' : html);
    if (!trimString(html)) return '';

    html = html.replace(/<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, '');
    html = html.replace(/<!--[\s\S]*?(?:-->|$)/g, '');

    var parts = html.split(/(<[^>]*>)/);
    var out = '';
    var open = [];

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (part === '') continue;

      // The HTML tokenizer's rule: only `<` + letter opens a tag, `</` + letter
      // closes one; `<!…`/`<?…`/`</3>` are swallowed, `< b >` and `<3` are text.
      if (part.charAt(0) !== '<' || !/^<\/?[a-zA-Z]/.test(part)) {
        if (part.charAt(0) === '<' && /^<[!?\/]/.test(part)) continue;
        out += part.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        continue;
      }

      var closeMatch = /^<\/([a-zA-Z][^\s\/>]*)/.exec(part);
      if (closeMatch) {
        out += closeInlineTag(closeMatch[1].toLowerCase(), open);
        continue;
      }

      var openMatch = /^<([a-zA-Z][^\s\/>]*)/.exec(part);
      if (!openMatch) continue;

      var tag = TABLE_CELL_INLINE_TAGS[openMatch[1].toLowerCase()];
      if (!tag) continue;

      if (tag === 'br') { out += '<br>'; continue; }

      if (tag === 'a') {
        var anchor = inlineAnchorOpenTag(part);
        if (anchor === null) {
          open.push({ tag: 'a', emitted: false });
          continue;
        }
        out += anchor;
        open.push({ tag: 'a', emitted: true });
        continue;
      }

      out += '<' + tag + '>';
      if (/\/\s*>$/.test(part)) {
        out += '</' + tag + '>';
        continue;
      }
      open.push({ tag: tag, emitted: true });
    }

    while (open.length > 0) {
      var entry = open.pop();
      if (entry.emitted) out += '</' + entry.tag + '>';
    }
    return out;
  }

  function tableAlignment(align) {
    var a = trimString(typeof align === 'string' ? align : '').toLowerCase();
    return (a === 'left' || a === 'center' || a === 'right') ? a : null;
  }

  // One <tr>. A row with no cells renders nothing; a row whose cells are all empty
  // strings still renders — an empty header row is what holds the columns open.
  function renderTableRow(row, isHeader, columns) {
    var cells = objectEntries(row);
    if (cells.length === 0) return '';
    var tag = isHeader ? 'th' : 'td';
    var baseStyle = isHeader ? TABLE_HEADER_CELL_STYLE : TABLE_CELL_STYLE;
    var html = '';
    for (var i = 0; i < cells.length; i++) {
      var cell = cells[i];
      var align = tableAlignment(cell.align) || tableAlignment(columns[i] && columns[i].align) || 'left';
      var attributes = ' style="' + baseStyle + ';text-align:' + align + '"';
      if (isHeader) attributes += ' scope="col"';
      var spans = { colSpan: 'colspan', rowSpan: 'rowspan' };
      for (var key in spans) {
        if (!Object.prototype.hasOwnProperty.call(spans, key)) continue;
        var span = cell[key];
        if (typeof span === 'string' && /^\d+$/.test(span)) span = parseInt(span, 10);
        if (typeof span === 'number' && isFinite(span) && span % 1 === 0 && span > 1) {
          attributes += ' ' + spans[key] + '="' + Math.min(span, 100) + '"';
        }
      }
      html += '<' + tag + attributes + '>' + sanitizeInlineHtml(cell.text) + '</' + tag + '>';
    }
    return html === '' ? '' : '<tr>' + html + '</tr>';
  }

  // <colgroup> for the per-column widths, or '' when no column asks for one. A
  // bare number is the HTML width attribute's meaning — pixels.
  function renderTableColumnGroup(columns) {
    var cols = '';
    var hasWidth = false;
    for (var i = 0; i < columns.length; i++) {
      var width = trimString(columns[i].width);
      if (width && /^\d+(\.\d+)?$/.test(width)) width += 'px';
      if (width && CSS_LENGTH_RE.test(width)) {
        cols += '<col style="width:' + escapeAttr(width) + '">';
        hasWidth = true;
      } else {
        cols += '<col>';
      }
    }
    return hasWidth ? '<colgroup>' + cols + '</colgroup>' : '';
  }

  // `content`: {rows: TableCell[][], headerRow: bool, columns?: [{width?, align?}], caption?}
  // `rows` INCLUDES the header row as rows[0]; `headerRow` says whether to render it
  // as one. Ragged rows stay ragged. The element's own styles.desktop go on the
  // scroll wrapper, after the overflow rule that keeps a five-column plan readable
  // on a phone.
  renderers.table = function (el) {
    var content = el.content || {};
    var rows = arrayEntries(content.rows);
    if (rows.length === 0) return '';
    var columns = objectEntries(content.columns);
    var hasHeaderRow = content.headerRow === true;

    var head = '';
    var body = '';
    for (var i = 0; i < rows.length; i++) {
      var isHeader = hasHeaderRow && i === 0;
      var tr = renderTableRow(rows[i], isHeader, columns);
      if (!tr) continue;
      if (isHeader) head += tr; else body += tr;
    }
    if (!head && !body) return '';

    var table = '<table style="' + TABLE_STYLE + '">';
    var caption = trimString(content.caption);
    if (caption) {
      table += '<caption style="caption-side:top;text-align:left;padding:0 0 8px;font-weight:600">' + escapeHtml(caption) + '</caption>';
    }
    table += renderTableColumnGroup(columns);
    if (head) table += '<thead>' + head + '</thead>';
    if (body) table += '<tbody>' + body + '</tbody>';
    table += '</table>';

    var apiStyles = USE_API_INLINE_STYLES ? stylesToInline(pickStyles(el)) : '';
    var wrapperStyle = 'overflow-x:auto;-webkit-overflow-scrolling:touch' + (apiStyles ? ';' + apiStyles : '');
    return '<div class="content-type-neo-table" style="' + wrapperStyle + '">' + table + '</div>';
  };

  // Height from styles.desktop.height when it is a plain CSS length, else 48px.
  renderers.spacer = function (el) {
    var height = trimString(pickStyles(el).height);
    if (!CSS_LENGTH_RE.test(height)) height = '48px';
    return '<div class="content-type-neo-spacer" style="height:' + escapeAttr(height) + '" aria-hidden="true"></div>';
  };

  // Badge text as plain text in a pill; style/size/align variants are cosmetic.
  renderers.badge = function (el) {
    var content = el.content || {};
    var text = trimString(content.text);
    if (!text) return '';
    return '<div class="content-type-neo-badge"' + inlineStyleAttr(el) + '><span class="badge">' + escapeHtml(text) + '</span></div>';
  };

  // `content`: {title?, subtitle?, items: [{name, fileUrl, fileType?, fileSize?}]}.
  // An item whose URL fails the allowlist keeps its label as text.
  renderers['file-list'] = function (el) {
    var content = el.content || {};
    var rawItems = objectEntries(content.items);
    var items = '';
    for (var i = 0; i < rawItems.length; i++) {
      var item = rawItems[i];
      var url = trimString(item.fileUrl);
      var name = trimString(item.name) || url;
      if (!name) continue;
      var meta = [];
      var fileType = trimString(item.fileType).toUpperCase();
      var fileSize = trimString(item.fileSize);
      if (fileType) meta.push(fileType);
      if (fileSize) meta.push(fileSize);
      var metaHtml = meta.length ? ' <span class="file-meta">(' + escapeHtml(meta.join(', ')) + ')</span>' : '';
      var label = escapeHtml(name) + metaHtml;
      items += '<li>' + (url && isSafeUrl(url)
        ? '<a href="' + escapeAttr(url) + '" download>' + label + '</a>'
        : '<span>' + label + '</span>') + '</li>';
    }
    if (!items) return '';
    var title = trimString(content.title);
    var subtitle = trimString(content.subtitle);
    return '<div class="content-type-neo-file-list"' + inlineStyleAttr(el) + '>' +
      (title ? '<h3>' + escapeHtml(title) + '</h3>' : '') +
      (subtitle ? '<p>' + escapeHtml(subtitle) + '</p>' : '') +
      '<ul>' + items + '</ul>' +
      '</div>';
  };

  // `content.items`: [{question, answer}] — native <details>, no script needed.
  renderers.faq = function (el) {
    var content = el.content || {};
    var rawItems = objectEntries(content.items);
    var items = '';
    for (var i = 0; i < rawItems.length; i++) {
      var question = trimString(rawItems[i].question);
      var answer = trimString(rawItems[i].answer);
      if (!question && !answer) continue;
      items += '<details><summary>' + escapeHtml(question) + '</summary>' +
        '<div class="faq-answer">' + escapeHtml(answer) + '</div></details>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-faq"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // `content.items`: [{title, content}] — the neo accordion, as NeoLandingPageBodyRenderer draws it:
  // native <details>/<summary>, collapsible with no script. Item bodies are author-edited rich text
  // (the builder's inline editor stores HTML) and ship verbatim like the text element; a title lives
  // inside <summary>, where block markup has no meaning, so it is reduced to its text first.
  // `content.allowMultiple` has no effect here for the same reason it has none in PHP: separate
  // <details> elements already open independently of one another.
  renderers.accordion = function (el) {
    var content = el.content || {};
    var rawItems = objectEntries(content.items);
    var items = '';
    for (var i = 0; i < rawItems.length; i++) {
      var title = trimString(decodeEntities(stripTags(rawItems[i].title)));
      var body = trimString(rawItems[i].content);
      if (!title && !body) continue;
      items += '<details><summary>' + escapeHtml(title) + '</summary>' +
        '<div class="accordion-content">' + body + '</div></details>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-accordion"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // `content`: {items: [{quote, author, role, rating}]} or the same keys flat on
  // content for a single testimonial.
  renderers.testimonial = function (el) {
    var content = el.content || {};
    var rawItems = objectEntries(content.items);
    if (rawItems.length === 0) {
      rawItems = [{
        quote: content.quote, author: content.author, role: content.role, avatar: content.avatar, rating: content.rating
      }];
    }
    var showRating = content.testimonialShowRating === undefined || content.testimonialShowRating === null
      ? true
      : !!content.testimonialShowRating;
    var items = '';
    for (var i = 0; i < rawItems.length; i++) {
      var item = rawItems[i];
      var quote = trimString(item.quote);
      var author = trimString(item.author);
      if (!quote && !author) continue;
      var itemHtml = '<blockquote class="testimonial">';
      if (quote) itemHtml += '<p>&ldquo;' + escapeHtml(quote) + '&rdquo;</p>';
      var rating = parseInt(item.rating, 10);
      if (!isFinite(rating)) rating = 0;
      if (showRating && rating > 0) {
        var stars = '';
        for (var s = 0; s < Math.min(rating, 5); s++) stars += '&#9733;';
        itemHtml += '<span class="rating" aria-label="' + rating + '/5">' + stars + '</span>';
      }
      if (author) {
        var role = trimString(item.role);
        itemHtml += '<footer><cite>' + escapeHtml(author) + '</cite>' +
          (role ? '<span class="role">' + escapeHtml(role) + '</span>' : '') +
          '</footer>';
      }
      items += itemHtml + '</blockquote>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-testimonial"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // ============================================================
  // linked-form — the AicoForm behind a `{formId, submitEndpoint}` element
  //
  // The server renderers (NeoLandingPageBodyRenderer and its news-popup subclass)
  // resolve the form from the database at render time. This engine has no database, so
  // it fetches the SAME allow-listed definition those renderers use — the anonymous
  // read endpoint GET /api/aiconeo/public/aico-forms/{id} — and builds the same markup
  // from it. One definition shape in, one form out, on all three surfaces.
  //
  // Two phases, because a renderer has to return a string synchronously: the renderer
  // emits the mount, hydrateLinkedForms() fills it when the fetch lands. A form that
  // cannot be resolved (deleted, offline, 404) leaves the SAME "neo: unrendered type"
  // marker an unknown type leaves and is counted in NewsNeo.lastUnrendered(). An empty
  // box where a registration form belongs is the exact failure this renderer exists to
  // prevent, so it must never be the quiet outcome.
  // ============================================================

  var LINKED_FORM_DEFINITION_PATH = '/api/aiconeo/public/aico-forms/';
  var LINKED_FORM_SUBMIT_PATH = '/api/v1/news/form-submissions';
  // ALTCHA is a sibling theme asset of this engine (same host, same CDN), never a third-party
  // CDN: a jsDelivr load would send the visitor's IP before consent. Resolved from this
  // script's own URL because the theme asset manifest is not visible to plain JS.
  var LINKED_FORM_ENGINE_SRC = (document.currentScript && document.currentScript.src) || '';
  var LINKED_FORM_ALTCHA_SRC = LINKED_FORM_ENGINE_SRC.replace(/[^\/?#]*([?#].*)?$/, 'altcha.min.js');
  var LINKED_FORM_ALTCHA_CHALLENGE = 'https://captcha.aico.swiss/altcha/challenge';
  // Each fraction gives up its share of the 16px column-gap, so a full row still adds up to
  // exactly 100%: n columns leave n-1 gaps, so each gives up 16px * (n-1) / n. Mirrors
  // NeoLandingPageBodyRenderer::LINKED_FORM_COLUMN_WIDTHS.
  var LINKED_FORM_COLUMN_WIDTHS = {
    QUARTER_ROW: 'calc(25% - 12px)', ONE_THIRD: 'calc(33.333333% - 10.666667px)',
    HALF_ROW: 'calc(50% - 8px)', FULL_ROW: '100%'
  };
  var LINKED_FORM_STYLE = 'max-width:640px;margin:0 auto';
  var LINKED_FORM_ROW_STYLE = 'display:flex;flex-wrap:wrap;column-gap:16px';
  var LINKED_FORM_ITEM_STYLE = 'display:flex;flex-direction:column;margin-bottom:12px';
  var LINKED_FORM_CONTROL_STYLE = 'width:100%;box-sizing:border-box;padding:8px;' +
    'border:1px solid #767676;border-radius:3px;font-size:15px';
  var LINKED_FORM_CHOICE_STYLE = 'display:flex;align-items:center;gap:6px;font-weight:400';
  // The id the legacy form Blade puts on its submit control, and the only hook a club theme has
  // for that button. An inline background outranks the theme rule and left the button invisible;
  // the defaults live in NEO_STYLES instead.
  var LINKED_FORM_SUBMIT_ID = 'submit-button';

  // Same allowlist as NeoLandingPageBodyRenderer::isSafeUrl(): control characters are
  // stripped first (browsers ignore them while resolving a scheme), then only http(s),
  // mailto, tel and scheme-less shapes are accepted.
  function isSafeLinkedFormUrl(url) {
    var normalized = String(url == null ? '' : url).replace(/[\x00-\x20\x7F]+/g, '');
    if (!normalized) return false;
    if (/^(?:https?|mailto|tel):/i.test(normalized)) return true;
    return !/^[^/?#]*:/.test(normalized);
  }

  renderers['linked-form'] = function (el) {
    var content = el.content || {};
    // `linkedFormId` is the pre-rename alias older documents still carry.
    var formId = String(content.formId != null ? content.formId : (content.linkedFormId != null ? content.linkedFormId : '')).trim();
    if (!/^\d+$/.test(formId)) return renderUnknown(el);

    var action = String(content.submitEndpoint || '').trim();
    if (!action || !isSafeLinkedFormUrl(action)) action = LINKED_FORM_SUBMIT_PATH;

    scheduleLinkedFormHydration();

    return '<div class="content-type-form content-type-neo-linked-form"' +
      ' data-aico-linked-form="' + escapeAttr(formId) + '"' +
      ' data-aico-linked-form-action="' + escapeAttr(action) + '"' +
      ' data-aico-linked-form-title="' + (content.showTitle === false ? '0' : '1') + '"' +
      ' data-neo-element-id="' + escapeAttr(el && el.id) + '"></div>';
  };

  function scheduleLinkedFormHydration() {
    if (typeof document === 'undefined' || typeof setTimeout !== 'function') return;
    setTimeout(hydrateLinkedForms, 0);
  }

  // The definition endpoint lives on the same host as the submit endpoint — that is the
  // AICO instance that owns the form, whether or not it is the host serving this page.
  function linkedFormDefinitionUrl(action, formId) {
    var origin = '';
    try {
      origin = new URL(action, typeof location !== 'undefined' ? location.href : undefined).origin;
    } catch (e) { origin = ''; }
    return origin + LINKED_FORM_DEFINITION_PATH + encodeURIComponent(formId);
  }

  // The forms this page has actually DRAWN, by form id. Object.create(null), not {}: the key is
  // an attribute read out of the DOM, and on a plain object a form id of `constructor` or
  // `toString` would inherit a truthy value and suppress a form nothing ever drew.
  //
  // Page-level and not per pass, because "once per page" is the rule: a later pass mounting the
  // same form must still see the copy the first pass drew.
  var drawnLinkedForms = Object.create(null);

  // ONE FORM, ONCE PER PAGE. A document can name the same AicoForm in two elements, and the
  // second is not a second form: it posts the same form_id under the same field names, and the
  // markup it would mount duplicates every control id and every label `for` target already on
  // the page, so a label click in the repeat moves the caret into the FIRST copy's input. Two
  // elements naming two DIFFERENT forms both hydrate, unchanged. Same rule and same word as the
  // server renderer's OMISSION_DUPLICATE_FORM, so one document reads the same whichever engine
  // drew it.
  function hydrateLinkedForms() {
    var queues = Object.create(null);
    var mounts = document.querySelectorAll('[data-aico-linked-form]:not([data-aico-linked-form-state])');
    for (var i = 0; i < mounts.length; i++) {
      var mount = mounts[i];
      var formId = mount.getAttribute('data-aico-linked-form') || '';
      // Claimed by THIS pass immediately, so a pass that runs while a mount is still waiting its
      // turn behind another cannot start a competing attempt on it. Overwritten the moment the
      // mount is actually taken up.
      mount.setAttribute('data-aico-linked-form-state', 'queued');
      if (!queues[formId]) queues[formId] = [];
      queues[formId].push(mount);
    }
    for (var queuedFormId in queues) hydrateLinkedFormQueue(queuedFormId, queues[queuedFormId]);
  }

  // ONE ATTEMPT AT A TIME PER FORM ID, and the queue collapses only onto a form that was really
  // DRAWN. That is the server renderer's rule exactly: it remembers a form AFTER it emits the
  // markup, so two references to a form that is GONE each report the loss, and only a repeat of
  // a form the reader can actually see is skipped.
  //
  // Claiming the id up front instead would undercount exactly that case: the first mount would
  // fail as `form-unavailable` and the second would be filed as a healthy `duplicate-form`, so
  // the page would report one missing form where the server reports two.
  function hydrateLinkedFormQueue(formId, queue) {
    if (queue.length === 0) return;
    var mount = queue.shift();
    if (drawnLinkedForms[formId]) {
      skipDuplicateLinkedForm(mount, formId);
      hydrateLinkedFormQueue(formId, queue);
      return;
    }
    hydrateOneLinkedForm(mount, function () { hydrateLinkedFormQueue(formId, queue); });
  }

  // Marked like an unrendered block but NOT tallied as one: the form is on the page, so the
  // reader is missing nothing and a pre-flight must not read this as content loss.
  //
  // The marker names the NEO ELEMENT id, which is what the server renderer puts in its own
  // marker, so a reader correlating the two engines by id gets the same element rather than two
  // different numbers. The form id stays reachable beside it, on `data-aico-linked-form`.
  function skipDuplicateLinkedForm(mount, formId) {
    mount.setAttribute('data-aico-linked-form-state', 'duplicate-form');
    var elementId = mount.getAttribute('data-neo-element-id') || formId;
    mount.innerHTML = unrenderedMarker('duplicate-form', { type: 'linked-form', id: elementId });
  }

  // `onSettled` runs once this mount has either drawn or failed, and is what lets the next
  // reference to the same form know whether it is a repeat or its own attempt.
  function hydrateOneLinkedForm(mount, onSettled) {
    var settled = function () { if (typeof onSettled === 'function') onSettled(); };
    mount.setAttribute('data-aico-linked-form-state', 'loading');
    var formId = mount.getAttribute('data-aico-linked-form') || '';
    var action = mount.getAttribute('data-aico-linked-form-action') || LINKED_FORM_SUBMIT_PATH;

    if (typeof fetch !== 'function') {
      failLinkedForm(mount, formId, 'no-fetch');
      settled();
      return;
    }

    fetch(linkedFormDefinitionUrl(action, formId), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var definition = payload && payload.data;
        var html = definition ? linkedFormHtml(definition, formId, action, mount.getAttribute('data-aico-linked-form-title') !== '0') : '';
        if (!html) throw new Error('empty definition');
        mount.innerHTML = html;
        fillLinkedFormRedirect(mount);
        drawnLinkedForms[formId] = true;
        mount.setAttribute('data-aico-linked-form-state', 'ready');
        loadAltchaOnce();
        settled();
      })
      .catch(function () {
        failLinkedForm(mount, formId, 'form-unavailable');
        settled();
      });
  }

  // A form that could not be resolved is REPORTED, never left as an empty box: the same
  // marker, the same tally and the same mount attribute an unknown element type gets.
  function failLinkedForm(mount, formId, reason) {
    tallyUnrendered('linked-form');
    mount.setAttribute('data-aico-linked-form-state', reason);
    mount.innerHTML = unrenderedMarker(reason, { type: 'linked-form', id: formId });
    try {
      var host = document.getElementById('aico-content');
      if (host) host.setAttribute('data-neo-unrendered', JSON.stringify(lastUnrendered()));
    } catch (e) { /* no-op */ }
    reportUnrendered();
  }

  // `redirect_to` decides where AicoFormsController::getRedirectUrl() sends the visitor after
  // the 302. Left empty it falls back to the `Referer`, and a cross-origin submission under the
  // browser default `strict-origin-when-cross-origin` sends the ORIGIN only, so the visitor lands
  // on the site's home page instead of the page they submitted from. innerHTML never executes a
  // <script>, so the server renderer's inline filler (LINKED_FORM_REDIRECT_SCRIPT) is inert here
  // and the field is filled directly instead, from the same two properties and the same guard.
  // Only an empty field is touched, so a value set on purpose wins.
  function fillLinkedFormRedirect(mount) {
    var field = mount.querySelector('input[name="redirect_to"]');
    if (field && !field.value) {
      field.value = window.location.origin + window.location.pathname;
    }
  }

  // <altcha-widget> is an inert unknown element without its module: the submission would
  // then carry no `altcha` payload, and AicoFormsController::checkCaptcha() refuses every
  // FCSG submission that has none. innerHTML never executes a <script>, so the loader is
  // appended as a real element instead — once per page, like the server renderer.
  function loadAltchaOnce() {
    if (document.querySelector('script[data-aico-altcha]')) return;
    var script = document.createElement('script');
    script.type = 'module';
    script.src = LINKED_FORM_ALTCHA_SRC;
    script.setAttribute('data-aico-altcha', '1');
    (document.head || document.body || document.documentElement).appendChild(script);
  }

  function linkedFormHtml(definition, formId, action, showTitle) {
    var elements = linkedFormElements(definition);
    var fields = '';
    for (var i = 0; i < elements.length; i++) fields += linkedFormElementHtml(elements[i]);
    if (!fields) return '';

    var html = '<form class="aico-custom-form aico-neo-linked-form" method="post"' +
      ' action="' + escapeAttr(action) + '" enctype="multipart/form-data"' +
      ' data-aico-form-id="' + escapeAttr(formId) + '" style="' + LINKED_FORM_STYLE + '">' +
      '<input type="hidden" name="form_id" value="' + escapeAttr(formId) + '">' +
      '<input type="hidden" name="redirect_to" value="">';

    var title = trimString(definition.name);
    if (showTitle && title) html += '<h3 class="aico-form-title">' + escapeHtml(title) + '</h3>';

    html += '<div class="aico-form-row" style="' + LINKED_FORM_ROW_STYLE + '">' + fields + '</div>' +
      '<altcha-widget auto="onsubmit" floating="auto" challengeurl="' + escapeAttr(LINKED_FORM_ALTCHA_CHALLENGE) + '"></altcha-widget>';

    var submitText = trimString(definition.submissionText) || 'Submit';
    return html + '<button type="submit" id="' + LINKED_FORM_SUBMIT_ID + '" class="aico-form-submit">' +
      escapeHtml(submitText) + '</button></form>';
  }

  // Hidden elements are dropped, the rest are ordered by `position` — the same rules as
  // NeoLandingPageBodyRenderer::linkedFormElements().
  function linkedFormElements(definition) {
    var raw = objectEntries(definition && definition.elements);
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      if (raw[i].isHidden === true) continue;
      if (!trimString(raw[i].key)) continue;
      out.push(raw[i]);
    }
    return out.sort(function (first, second) {
      return (parseInt(first.position, 10) || 0) - (parseInt(second.position, 10) || 0);
    });
  }

  function linkedFormElementHtml(element) {
    var key = trimString(element.key);
    var type = trimString(element.type).toLowerCase() || 'text';
    var config = (element.config && typeof element.config === 'object') ? element.config : {};
    var isRequired = element.isRequired === true;
    var label = trimString(element.label);
    var placeholder = trimString(element.placeholder);

    // `config.title` is stored MARKUP, not a string: the legacy Blade printed it raw inside a
    // plain `form-item` wrapper and never wore an `<h4 class="aico_label">`. Escaping it showed
    // the reader the tags, and every stored value measured carried block-level markup an `<h4>`
    // cannot legally hold. Same trust level as the `html` element just below; see
    // NeoLandingPageBodyRenderer::renderLinkedFormElement(), which this mirrors byte for byte.
    if (type === 'title') {
      var titleMarkup = trimString(config.title) || label;
      return titleMarkup
        ? linkedFormColumn(element, '<div class="form-item" style="' + LINKED_FORM_ITEM_STYLE + '">' + titleMarkup + '</div>')
        : '';
    }
    if (type === 'html') {
      var markup = trimString(config.html);
      return markup ? linkedFormColumn(element, markup) : '';
    }

    var control = linkedFormControlHtml(type, key, config, isRequired, placeholder);
    if (!control) return '';

    var labelHtml = '';
    if (label) {
      labelHtml = '<label class="form-label" for="' + escapeAttr(key) + '">' +
        '<h4 class="aico_label">' + escapeHtml(label) +
        (isRequired ? '<span class="aico_required"> *</span>' : '') +
        '</h4></label>';
    }

    return linkedFormColumn(element,
      '<div class="form-item" style="' + LINKED_FORM_ITEM_STYLE + '">' + labelHtml + control + '</div>');
  }

  function linkedFormControlHtml(type, key, config, isRequired, placeholder) {
    var required = isRequired ? ' required' : '';
    var requiredClass = isRequired ? ' is_required' : '';
    var placeholderAttr = placeholder ? ' placeholder="' + escapeAttr(placeholder) + '"' : '';
    var style = ' style="' + LINKED_FORM_CONTROL_STYLE + '"';

    function inputHtml(inputType) {
      return '<input type="' + inputType + '" name="' + escapeAttr(key) + '"' +
        ' id="' + escapeAttr(key) + '" class="input-select' + requiredClass + '"' +
        placeholderAttr + style + required + '>';
    }

    switch (type) {
      case 'email': return inputHtml('email');
      case 'phone_number': return inputHtml('tel');
      case 'date_picker': return inputHtml('date');
      case 'textarea':
      case 'text_area':
        return '<textarea name="' + escapeAttr(key) + '" id="' + escapeAttr(key) + '" rows="4"' +
          ' class="input-select' + requiredClass + '"' + placeholderAttr + style + required + '></textarea>';
      case 'file_upload': return inputHtml('file');
      case 'multiple_files_upload':
      case 'multiple_file_upload':
        return '<input type="file" name="' + escapeAttr(key) + '[]" id="' + escapeAttr(key) + '" multiple' +
          ' class="input-select' + requiredClass + '"' + style + required + '>';
      case 'dropdown':
      case 'slider':
        return linkedFormSelectHtml(key, config, isRequired, placeholder);
      case 'radio': return linkedFormChoicesHtml('radio', key, config, isRequired);
      case 'checkbox': return linkedFormChoicesHtml('checkbox', key, config, isRequired);
      // text, country, zip_code and anything the form builder grows later: a plain text
      // input still collects the answer under the right key — degraded, never lost.
      default: return inputHtml('text');
    }
  }

  function linkedFormSelectHtml(key, config, isRequired, placeholder) {
    var isMultiple = config.multipleEnabled === true;
    var name = isMultiple ? escapeAttr(key) + '[]' : escapeAttr(key);
    var options = '<option value="">' + escapeHtml(placeholder || '-') + '</option>';
    var values = linkedFormOptions(config);
    for (var i = 0; i < values.length; i++) {
      options += '<option value="' + escapeAttr(values[i].value) + '">' + escapeHtml(values[i].label) + '</option>';
    }
    return '<select name="' + name + '" id="' + escapeAttr(key) + '"' +
      ' class="input-select' + (isRequired ? ' is_required' : '') + '"' +
      ' style="' + LINKED_FORM_CONTROL_STYLE + '"' +
      (isMultiple ? ' multiple' : '') + (isRequired ? ' required' : '') + '>' + options + '</select>';
  }

  function linkedFormChoicesHtml(inputType, key, config, isRequired) {
    var options = linkedFormOptions(config);
    var name = inputType === 'checkbox' ? escapeAttr(key) + '[]' : escapeAttr(key);
    if (!options.length && inputType === 'checkbox') {
      options = [{ value: '1', label: '' }];
      name = escapeAttr(key);
    }
    if (!options.length) return '';

    var html = '<div class="' + inputType + '-group' + (isRequired ? ' required' : '') + '" id="' + escapeAttr(key) + '">';
    for (var i = 0; i < options.length; i++) {
      var optionId = escapeAttr(key) + '-' + i;
      html += '<label for="' + optionId + '" style="' + LINKED_FORM_CHOICE_STYLE + '">' +
        '<input type="' + inputType + '" name="' + name + '" id="' + optionId + '"' +
        ' value="' + escapeAttr(options[i].value) + '"' +
        (isRequired && inputType === 'radio' ? ' required' : '') + '> ' + escapeHtml(options[i].label) +
        '</label>';
    }
    return html + '</div>';
  }

  function linkedFormOptions(config) {
    var raw = objectEntries(config && config.possibleValues);
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var value = trimString(raw[i].value);
      if (!value) continue;
      out.push({ value: value, label: trimString(raw[i].label) || value });
    }
    return out;
  }

  function linkedFormColumn(element, inner) {
    var size = trimString(element.columnSize).toUpperCase();
    var width = LINKED_FORM_COLUMN_WIDTHS[size] || '100%';
    return '<div class="aico-form-column" data-column-size="' + escapeAttr(size || 'FULL_ROW') + '"' +
      ' style="box-sizing:border-box;flex:0 0 ' + width + ';max-width:' + width + '">' +
      inner + '</div>';
  }

  // ============================================================
  // Unrendered types — observable, never silent
  //
  // A type without a renderer used to be a console.warn and an empty string, which
  // is how a migrated table could vanish from a live article with nothing in the
  // page to show for it. Now every such element leaves an HTML comment marker where
  // it stood, render() tallies them, one aggregated warning names them, the page
  // dispatches a `neo:unrendered` event, and the bootstrap stamps the tally onto
  // the mount as `data-neo-unrendered` — greppable in view-source, visible to a
  // DOM check, reachable through NewsNeo.lastUnrendered().
  // ============================================================

  var unrenderedTally = null;

  function tallyUnrendered(type) {
    if (!unrenderedTally) unrenderedTally = {};
    unrenderedTally[type] = (unrenderedTally[type] || 0) + 1;
  }

  function lastUnrendered() {
    var out = {};
    if (!unrenderedTally) return out;
    for (var k in unrenderedTally) {
      if (Object.prototype.hasOwnProperty.call(unrenderedTally, k)) out[k] = unrenderedTally[k];
    }
    return out;
  }

  // Comment-safe token: no `--`, nothing that could close the comment early.
  function commentToken(value) {
    return String(value == null ? '' : value).replace(/[^A-Za-z0-9_.:-]/g, '').replace(/--+/g, '-') || 'unknown';
  }

  function unrenderedMarker(reason, el) {
    return '<!-- neo: unrendered type "' + commentToken(el && el.type) + '" id="' + commentToken(el && el.id) +
      '" reason="' + reason + '" -->';
  }

  function isMarkerOnly(html) {
    return /^<!--[\s\S]*-->$/.test(html);
  }

  function reportUnrendered() {
    var types = Object.keys(lastUnrendered());
    if (types.length === 0) return;
    var tally = lastUnrendered();
    var summary = types.sort().map(function (t) { return t + ' x' + tally[t]; }).join(', ');
    if (window.console && console.warn) {
      console.warn('NewsNeo: ' + types.length + ' element type(s) on this page have no renderer: ' + summary +
        '. Search the DOM for "neo: unrendered type" to locate them.');
    }
    try {
      if (typeof document !== 'undefined' && typeof CustomEvent === 'function' && document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('neo:unrendered', { detail: { types: tally } }));
      }
    } catch (e) { /* no-op */ }
  }

  // ============================================================
  // Structural `container`, plus the three content types the registry was
  // missing: `features`, `map`, `social-embed`.
  //
  // All four are emitted by the legacy -> neo converters and all four fell
  // through renderUnknown(). `container` is the expensive one: it carries its
  // payload in `children`, so losing it lost every element nested inside it,
  // not one block.
  //
  // Each arm mirrors its counterpart in NeoLandingPageBodyRenderer
  // (renderContainer / renderFeatures / renderMap / renderSocialEmbed), because
  // the server and the browser draw the SAME document and a theme stylesheet
  // has to fit both.
  //
  // The helpers are deliberately self-contained (`neo*` names): this block is
  // the same text in every copy of the engine, and the older copies do not
  // carry trimString() / objectEntries() / isSafeUrl().
  // ============================================================

  function neoTrim(value) {
    return value == null ? '' : String(value).trim();
  }

  // Entries of a list-valued content field that are plain objects, tolerating junk.
  function neoObjectList(value) {
    if (!Array.isArray(value)) return [];
    var out = [];
    for (var i = 0; i < value.length; i++) {
      if (value[i] && typeof value[i] === 'object' && !Array.isArray(value[i])) out.push(value[i]);
    }
    return out;
  }

  // Allowlist of inert URL shapes, mirroring the PHP renderer's isSafeUrl():
  // control characters are stripped first (browsers ignore them when resolving a
  // scheme, so "java\tscript:" would otherwise slip a denylist), then only
  // http(s)/mailto/tel and scheme-less URLs pass.
  function neoSafeUrl(url) {
    var normalized = String(url == null ? '' : url).replace(/[\x00-\x20\x7F]+/g, '');
    if (!normalized) return false;
    if (/^(?:https?|mailto|tel):/i.test(normalized)) return true;
    return !/^[^\/?#]*:/.test(normalized);
  }

  // isSafeUrl plus the inline base64 images the builder can store (PHP:
  // isSafeImageSource()).
  function neoSafeImageSource(url) {
    if (neoSafeUrl(url)) return true;
    // `(?![\s\S])` and not `$`: JavaScript's `$` also matches before a trailing
    // newline, so "data:...==\n" would pass here and be refused by the PHP twin,
    // whose `\z` is an absolute end.
    return /^data:image\/(?:png|jpeg|jpg|gif|webp|avif);base64,[A-Za-z0-9+\/]+={0,2}(?![\s\S])/i
      .test(String(url == null ? '' : url));
  }

  // What a `container`'s content.htmlAttributes may carry onto the wrapper tag:
  // the same allowlist as NeoLandingPageBodyRenderer::ANCHOR_ATTRIBUTE_PATTERN —
  // an id a club theme's own script selects, plus the harmless describe/label
  // attributes.
  var NEO_CONTAINER_ATTRIBUTE = /^(?:id|title|data-[a-zA-Z0-9_.:-]+|aria-[a-zA-Z]+)$/;

  // A marker comment is a TRACE, not content: a container holding nothing but
  // markers is empty and must not keep a shell around them. Mirrors the PHP
  // renderer's hasRenderedContent().
  var NEO_MARKER_PATTERN = /<!-- neo: unrendered type "[^"]*" id="[^"]*" reason="[^"]*" -->/g;

  function neoHasRenderedContent(html) {
    return String(html == null ? '' : html).replace(NEO_MARKER_PATTERN, '').trim() !== '';
  }

  // A container's payload is its CHILDREN, not its content. The AI builder writes
  // `children`, the legacy Grapes converter writes `elements`; both are walked,
  // exactly as on the PHP side.
  function neoContainerChildren(el) {
    var children = el && (el.children || el.elements);
    return Array.isArray(children) ? children : [];
  }

  // Children concatenated. `columns` is laid out as its own row here because
  // renderElement() refuses that type on purpose: renderSection owns it at
  // section level, and a container is not a section.
  function renderChildElements(children) {
    var html = '';
    var rowIndex = 1;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (!child || typeof child !== 'object') continue;
      if (child.type === 'columns') {
        var row = renderColumnsRow(child, rowIndex, false);
        if (row) { html += row; rowIndex++; }
        continue;
      }
      html += renderElement(child);
    }
    return html;
  }

  // NO inline styles on the wrapper, deliberately: the PHP twin emits none
  // either, and a container that paints a background on one renderer and not on
  // the other is one document drawn two ways. Element styles inside it are
  // unaffected.
  function renderContainer(el) {
    var content = (el && el.content) || {};
    var inner = renderChildElements(neoContainerChildren(el));

    var classes = [];
    if (Array.isArray(content.cssClasses)) {
      for (var i = 0; i < content.cssClasses.length; i++) {
        var name = content.cssClasses[i] == null ? '' : String(content.cssClasses[i]);
        if (name !== '') classes.push(name);
      }
    }

    var attributes = [];
    var rawAttributes = content.htmlAttributes;
    if (rawAttributes && typeof rawAttributes === 'object' && !Array.isArray(rawAttributes)) {
      for (var key in rawAttributes) {
        if (!Object.prototype.hasOwnProperty.call(rawAttributes, key)) continue;
        var value = rawAttributes[key];
        if (value == null || typeof value === 'object' || typeof value === 'function') continue;
        if (!NEO_CONTAINER_ATTRIBUTE.test(key)) continue;
        attributes.push({ name: key, value: String(value) });
      }
    }

    // An ANCHOR container is one the converter marked: it carries the authored
    // class list, or an id the theme selects on. It keeps its own tag and
    // survives empty, because an empty anchor is still the thing a link jumps to.
    var isAnchor = classes.length > 0 || attributes.length > 0;
    if (!neoHasRenderedContent(inner) && !isAnchor) return inner;

    var tag = 'div';
    if (isAnchor) {
      var candidate = String(content.tag == null ? 'div' : content.tag).toLowerCase();
      if (/^[a-z][a-z0-9-]*$/.test(candidate)) tag = candidate;
    }

    var html = '<' + tag + ' class="' +
      escapeAttr(isAnchor ? classes.join(' ') : 'content-type-neo-container') + '"';
    for (var j = 0; j < attributes.length; j++) {
      html += ' ' + attributes[j].name + '="' + escapeAttr(attributes[j].value) + '"';
    }

    return html + '>' + inner + '</' + tag + '>';
  }

  // The builder's icon set as SVG geometry, so a tile's icon reaches the live
  // page. lucide's own (lucide-react 0.577.0, ISC), copied from
  // NeoLandingPageBodyRenderer::ICON_GEOMETRY rather than redrawn, so the
  // storefront draws the glyph the author picked and not an approximation.
  var NEO_ICON_GEOMETRY = {
    "Award": "<path d=\"m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526\"/><circle cx=\"12\" cy=\"8\" r=\"6\"/>",
    "BarChart3": "<path d=\"M3 3v16a2 2 0 0 0 2 2h16\"/><path d=\"M18 17V9\"/><path d=\"M13 17V5\"/><path d=\"M8 17v-3\"/>",
    "Calendar": "<path d=\"M8 2v4\"/><path d=\"M16 2v4\"/><rect width=\"18\" height=\"18\" x=\"3\" y=\"4\" rx=\"2\"/><path d=\"M3 10h18\"/>",
    "Camera": "<path d=\"M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z\"/><circle cx=\"12\" cy=\"13\" r=\"3\"/>",
    "Check": "<path d=\"M20 6 9 17l-5-5\"/>",
    "Clock": "<circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"M12 6v6l4 2\"/>",
    "Eye": "<path d=\"M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0\"/><circle cx=\"12\" cy=\"12\" r=\"3\"/>",
    "Gift": "<path d=\"M12 7v14\"/><path d=\"M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8\"/><path d=\"M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5\"/><rect x=\"3\" y=\"7\" width=\"18\" height=\"4\" rx=\"1\"/>",
    "Globe": "<circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20\"/><path d=\"M2 12h20\"/>",
    "Headphones": "<path d=\"M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3\"/>",
    "Heart": "<path d=\"M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5\"/>",
    "Lock": "<rect width=\"18\" height=\"11\" x=\"3\" y=\"11\" rx=\"2\" ry=\"2\"/><path d=\"M7 11V7a5 5 0 0 1 10 0v4\"/>",
    "Mail": "<path d=\"m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7\"/><rect x=\"2\" y=\"4\" width=\"20\" height=\"16\" rx=\"2\"/>",
    "MapPin": "<path d=\"M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0\"/><circle cx=\"12\" cy=\"10\" r=\"3\"/>",
    "Package": "<path d=\"M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z\"/><path d=\"M12 22V12\"/><polyline points=\"3.29 7 12 12 20.71 7\"/><path d=\"m7.5 4.27 9 5.15\"/>",
    "Phone": "<path d=\"M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384\"/>",
    "RefreshCw": "<path d=\"M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8\"/><path d=\"M21 3v5h-5\"/><path d=\"M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16\"/><path d=\"M8 16H3v5\"/>",
    "Shield": "<path d=\"M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z\"/>",
    "Star": "<path d=\"M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z\"/>",
    "ThumbsUp": "<path d=\"M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z\"/><path d=\"M7 10v12\"/>",
    "Ticket": "<path d=\"M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z\"/><path d=\"M13 5v2\"/><path d=\"M13 17v2\"/><path d=\"M13 11v2\"/>",
    "Trophy": "<path d=\"M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978\"/><path d=\"M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978\"/><path d=\"M18 9h1.5a1 1 0 0 0 0-5H18\"/><path d=\"M4 22h16\"/><path d=\"M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z\"/><path d=\"M6 9H4.5a1 1 0 0 1 0-5H6\"/>",
    "Truck": "<path d=\"M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2\"/><path d=\"M15 18H9\"/><path d=\"M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14\"/><circle cx=\"17\" cy=\"18\" r=\"2\"/><circle cx=\"7\" cy=\"18\" r=\"2\"/>",
    "Users": "<path d=\"M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2\"/><path d=\"M16 3.128a4 4 0 0 1 0 7.744\"/><path d=\"M22 21v-2a4 4 0 0 0-3-3.87\"/><circle cx=\"9\" cy=\"7\" r=\"4\"/>",
  };

  var NEO_ICON_SVG_OPEN = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"' +
    ' width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">';

  // An uploaded image, a named icon from the palette, or '' when the item
  // carries neither. An icon the palette does not know draws NOTHING rather than
  // a placeholder glyph: a wrong pictogram says something the author did not.
  function neoTileIcon(item, size) {
    var name = neoTrim(item && item.icon);
    var iconUrl = neoTrim(item && item.iconUrl);

    if (name === 'custom' || iconUrl !== '') {
      if (iconUrl === '' || !neoSafeImageSource(iconUrl)) return '';
      return '<img src="' + escapeAttr(iconUrl) + '" alt="" loading="lazy" style="' +
        escapeAttr('width:' + size + 'px;height:' + size + 'px;object-fit:contain') + '">';
    }

    if (!Object.prototype.hasOwnProperty.call(NEO_ICON_GEOMETRY, name)) return '';

    // The size lands on the <svg> itself, overriding the frame's own 24.
    return NEO_ICON_SVG_OPEN.replace('width="24" height="24"',
      'width="' + size + '" height="' + size + '"') + NEO_ICON_GEOMETRY[name] + '</svg>';
  }

  // A linked feature tile keeps the tile's own look: the theme's link colour and
  // underline on a whole card would repaint the card.
  var NEO_FEATURE_LINK_STYLE = 'display:block;color:inherit;text-decoration:none';
  var NEO_FEATURE_ICON_STYLE = 'display:inline-flex;align-items:center;justify-content:center' +
    ';width:48px;height:48px;border-radius:9999px;background-color:rgba(0,0,0,0.06)';

  // `content.items`: [{icon, iconUrl?, title, description, image?, link?}].
  // The whole tile is the link when the author gave one — that is what the
  // designs built out of this element draw (a Kids-Club tile is one destination),
  // and it is what the PHP renderer does.
  renderers.features = function (el) {
    var content = (el && el.content) || {};
    var items = '';
    var list = neoObjectList(content.items);

    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      var title = neoTrim(item.title);
      var description = neoTrim(item.description);
      if (title === '' && description === '') continue;

      var link = neoTrim(item.link);
      var isLinked = link !== '' && neoSafeUrl(link);
      var itemHtml = isLinked
        ? '<a class="feature" href="' + escapeAttr(link) + '" style="' + escapeAttr(NEO_FEATURE_LINK_STYLE) + '">'
        : '<div class="feature">';

      // An item's image REPLACES its icon rather than joining it, in that order,
      // because that is the choice the builder canvas makes.
      var image = neoTrim(item.image);
      if (image !== '' && neoSafeImageSource(image)) {
        itemHtml += '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy">';
      } else {
        var icon = neoTileIcon(item, 24);
        if (icon !== '') itemHtml += '<span style="' + escapeAttr(NEO_FEATURE_ICON_STYLE) + '">' + icon + '</span>';
      }

      if (title !== '') itemHtml += '<h3>' + escapeAttr(title) + '</h3>';
      if (description !== '') itemHtml += '<p>' + escapeAttr(description) + '</p>';
      items += itemHtml + (isLinked ? '</a>' : '</div>');
    }

    if (items === '') return '';

    return '<div class="content-type-neo-features"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // `content`: {embedUrl?, address?, zoom?, height?}. An author's embed URL is
  // used when it is a Google Maps one; otherwise the address becomes the query
  // embed. Unconfigured renders nothing.
  renderers.map = function (el) {
    var content = (el && el.content) || {};
    var embedUrl = neoTrim(content.embedUrl);
    var address = neoTrim(content.address);

    var source = '';
    if (embedUrl !== '' && /^https:\/\/(www\.)?(google\.[a-z.]{2,6}\/maps|maps\.google\.[a-z.]{2,6})/i.test(embedUrl)) {
      source = embedUrl;
    } else if (address !== '') {
      var zoom = content.zoom == null ? 13 : Math.floor(Number(content.zoom));
      if (!isFinite(zoom)) zoom = 0;
      zoom = Math.max(1, Math.min(zoom, 21));
      source = 'https://www.google.com/maps?q=' + encodeURIComponent(address) + '&z=' + zoom + '&output=embed';
    }

    if (source === '') return '';

    var height = content.height == null ? 400 : Math.floor(Number(content.height));
    if (!isFinite(height) || height < 100 || height > 2000) height = 400;

    return '<div class="content-type-neo-map" style="height:' + height + 'px">' +
      '<iframe src="' + escapeAttr(source) + '" title="Google Maps" width="100%" height="100%"' +
      ' style="border:0" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>' +
      '</div>';
  };

  // `content`: {embedCode?, embedUrl?}. The embed code is author-pasted platform
  // markup — the builder canvas injects it verbatim, the same trust level as the
  // `html` element, so it ships verbatim here too.
  renderers['social-embed'] = function (el) {
    var content = (el && el.content) || {};
    var embedCode = neoTrim(content.embedCode);
    if (embedCode !== '') {
      return '<div class="content-type-neo-social-embed">' + embedCode + '</div>';
    }

    var embedUrl = neoTrim(content.embedUrl);
    if (embedUrl === '' || !/^https:\/\//i.test(embedUrl)) return '';

    return '<div class="content-type-neo-social-embed">' +
      '<iframe src="' + escapeAttr(embedUrl) + '" title="Social Media" width="100%" height="480"' +
      ' style="border:0" loading="lazy" allowfullscreen></iframe>' +
      '</div>';
  };

  function renderUnknown(el) {
    tallyUnrendered(el && typeof el.type === 'string' ? el.type : 'unknown');
    return unrenderedMarker('no-renderer', el);
  }

  // ============================================================
  // Dispatch (single element, NOT a column container)
  // ============================================================

  function renderElement(el) {
    if (!el || typeof el !== 'object') return '';
    if (el.type === 'columns') {
      // Should never reach here - handled in renderSection. Safe guard.
      return '';
    }
    // `container` is structural, like `columns`: its payload is children, not
    // content, so it never reaches the `renderers` registry. It is resolved to a
    // function HERE rather than dispatched above so that it runs inside the same
    // try/catch as every other type — a throw in renderContainer() would
    // otherwise leave render() and blank the whole page, when the rule this
    // engine holds is that a failure costs one block and leaves a marker.
    var fn = el.type === 'container' ? renderContainer : renderers[el.type];
    if (typeof fn !== 'function') return renderUnknown(el);
    try {
      return fn(el);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('NewsNeo: renderer for "' + el.type + '" threw', e);
      }
      tallyUnrendered(el.type);
      return unrenderedMarker('renderer-threw', el);
    }
  }

  // ============================================================
  // Row / column wrappers
  // Mirrors the legacy structure produced inside news.js so news.css
  // styles the result identically.
  // ============================================================

  function wrapContent(innerHtml, contentIndex) {
    if (!innerHtml) return '';
    // An unrendered-type marker stays in the DOM but gets no content wrapper, so it
    // neither shifts content__N numbering nor leaves an empty block in the flow.
    if (isMarkerOnly(innerHtml)) return innerHtml;
    return '<div class="content content__' + contentIndex + '">' + innerHtml + '</div>';
  }

  function wrapColumn(contentHtmlList, columnIndex) {
    var inner = contentHtmlList.join('');
    return '<div class="content-column column__' + columnIndex + '">' + inner + '</div>';
  }

  function wrapRow(columnsHtml, rowIndex, totalColumns, isFullWidth) {
    var classes = ['content-row', 'row__' + rowIndex, 'total-columns-' + totalColumns];
    if (isFullWidth) classes.push('fullwidth');
    return '<div class="' + classes.join(' ') + '">' + columnsHtml + '</div>';
  }

  // Render a `columns` element as one multi-column row.
  function renderColumnsRow(columnsEl, rowIndex, isFullWidth) {
    var content = columnsEl.content || {};
    var count = parseInt(content.columnCount, 10);
    if (isNaN(count) || count < 1) count = 2;
    if (count > 4) count = 4; // legacy CSS supports up to total-columns-4

    var children = Array.isArray(columnsEl.children) ? columnsEl.children : [];

    var sorted = children.slice().sort(function (a, b) {
      var ai = (a && a.content && typeof a.content.columnIndex === 'number') ? a.content.columnIndex : 0;
      var bi = (b && b.content && typeof b.content.columnIndex === 'number') ? b.content.columnIndex : 0;
      return ai - bi;
    });

    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);
    var fallbackIdx = 0;
    for (var j = 0; j < sorted.length; j++) {
      var child = sorted[j];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    for (var col = 0; col < count; col++) {
      var contentsHtml = [];
      for (var k = 0; k < buckets[col].length; k++) {
        var rendered = renderElement(buckets[col][k]);
        if (rendered) contentsHtml.push(wrapContent(rendered, k + 1));
      }
      columnsHtml += wrapColumn(contentsHtml, col + 1);
    }

    return wrapRow(columnsHtml, rowIndex, count, isFullWidth);
  }

  // Emits N rows per section: consecutive non-columns elements stack into one row;
  // each `columns` element becomes its own multi-column row, in document order.
  function renderSection(section, startRowIndex) {
    if (!section || !Array.isArray(section.elements) || section.elements.length === 0) {
      return { html: '', rowCount: 0 };
    }

    var isFullWidth = !!section.fullWidth;
    var elements = section.elements;
    var html = '';
    var rowIndex = startRowIndex;
    var buffer = [];

    function flushBuffer() {
      if (buffer.length === 0) return;
      var singleCol = wrapColumn(buffer, 1);
      html += wrapRow(singleCol, rowIndex++, 1, isFullWidth);
      buffer = [];
    }

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;
      if (el.type === 'columns') {
        flushBuffer();
        var columnsRow = renderColumnsRow(el, rowIndex, isFullWidth);
        if (columnsRow) { html += columnsRow; rowIndex++; }
      } else {
        var rendered = renderElement(el);
        if (rendered) buffer.push(wrapContent(rendered, buffer.length + 1));
      }
    }
    flushBuffer();

    return { html: html, rowCount: rowIndex - startRowIndex };
  }

  function render(data) {
    if (!hasNeoContent(data)) return '';
    var neo = data.contentBuilderNeoJson;
    unrenderedTally = null;
    currentTokens = (neo.designTokens && typeof neo.designTokens === 'object') ? neo.designTokens : null;

    var html = '';
    var nextRowIndex = 1;
    for (var i = 0; i < neo.sections.length; i++) {
      var result = renderSection(neo.sections[i], nextRowIndex);
      html += result.html;
      nextRowIndex += result.rowCount;
    }
    reportUnrendered();
    return html;
  }

  // ============================================================
  // Self-contained styles for slideshow/video/youtube/gallery.
  // Injected once per page so the renderer ships its own CSS.
  // ============================================================

  var STYLES_INJECTED = false;
  var NEO_STYLES = [
    // Baseline CTA look for the builder's button element: a styled button even
    // when the document carries no design tokens. Token/element styles land
    // inline on the <a> and override this.
    '.content-type-neo-button{margin:8px 0;}',
    '.content-type-neo-button .button{display:inline-block;padding:12px 28px;background:#e8702d;color:#fff;font-weight:600;text-decoration:none;border-radius:var(--aico-radius,2px);line-height:1.2;transition:filter .12s ease;}',
    '.content-type-neo-button .button:hover{filter:brightness(0.92);}',
    '.neo-slideshow__viewport{position:relative;overflow:hidden;border-radius:inherit;}',
    '.neo-slideshow__track{display:flex;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none;}',
    '.neo-slideshow__track::-webkit-scrollbar{display:none;}',
    '.neo-slideshow__slide{flex:0 0 100%;height:100%;scroll-snap-align:start;position:relative;}',
    '.neo-slideshow__slide img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.neo-slideshow__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.5);color:#fff;border:0;width:36px;height:36px;border-radius:50%;cursor:pointer;z-index:1;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;}',
    '.neo-slideshow__nav--prev{left:8px;}',
    '.neo-slideshow__nav--next{right:8px;}',
    '.neo-slideshow__dots{display:flex;justify-content:center;gap:8px;margin-top:8px;}',
    '.neo-slideshow__dot{width:8px;height:8px;border-radius:50%;border:0;background:#ccc;cursor:pointer;padding:0;}',
    '.neo-slideshow__dot.is-active{background:#333;}',
    '.neo-slideshow__caption{position:absolute;inset:0;padding:24px;background:rgba(0,0,0,0.35);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:12px;}',
    '.neo-slideshow__headline{margin:0;color:#fff !important;font-size:28px;font-weight:600;line-height:1.2;}',
    '.neo-slideshow__btn{display:inline-block;padding:8px 16px;background:#fff;color:#111;text-decoration:none;border-radius:4px;}',
    '.neo-video__frame{position:relative;width:100%;}',
    '.neo-video__frame iframe,.neo-video__frame video{position:absolute;inset:0;width:100%;height:100%;border:0;}',
    '.content-type-neo-image img{max-width:100%;height:auto;display:block;}',
    // overflow:hidden so wrapper border-radius actually clips the iframe/slides inside.
    '.content-type-neo-youtube,.content-type-neo-video,.content-type-neo-slideshow{overflow:hidden;}',
    '.content-type-neo-gallery{display:grid;gap:8px;}',
    '.content-type-neo-gallery img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__title{margin:0 0 16px;}',
    '.neo-media-gallery__grid{display:grid;gap:8px;}',
    '.neo-media-gallery__item{margin:0;}',
    '.neo-media-gallery__item img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__caption{font-size:12px;color:#555;margin-top:4px;}',
    // Baseline look for the converter-emitted static types (table cells carry
    // their own inline styles, like the PHP renderer). Theme CSS may override.
    '.content-type-neo-badge .badge{display:inline-block;padding:4px 12px;border-radius:999px;background:#e8702d;color:#fff;font-size:12px;font-weight:600;line-height:1.4;}',
    '.content-type-neo-file-list ul{list-style:none;margin:0;padding:0;}',
    '.content-type-neo-file-list li{padding:8px 0;border-bottom:1px solid #e5e5e5;}',
    '.content-type-neo-file-list .file-meta{color:#777;font-size:12px;}',
    '.content-type-neo-faq details{padding:8px 0;border-bottom:1px solid #e5e5e5;}',
    '.content-type-neo-faq summary{cursor:pointer;font-weight:600;}',
    '.content-type-neo-faq .faq-answer{padding:8px 0 0;}',
    '.content-type-neo-accordion details{padding:8px 0;border-bottom:1px solid #e5e5e5;}',
    '.content-type-neo-accordion summary{cursor:pointer;font-weight:600;}',
    '.content-type-neo-accordion .accordion-content{padding:8px 0 0;}',
    // Linked form: the submit control and the field label no longer carry inline paint, because
    // an inline declaration outranks the club theme rule that is the only thing making the button
    // visible. These are the legacy form Blade's own declarations, kept as the baseline for a
    // page whose theme styles neither. Mirrors NeoBodyStylesheet.
    //
    // All three are wrapped in :where(), so all three are specificity ZERO and the club theme wins
    // whatever it writes. The id needs it most (bare, it is (1,0,0) and outranks a theme rule
    // written with classes alone), but the two class rules need it as well: they are (0,2,0), the
    // same weight as the theme's own `.aico-custom-form .aico_label`, and this <style> is injected
    // at runtime AFTER the theme's stylesheet, so at equal specificity source order would hand the
    // tie to the fallback and override the theme's own label. The PHP side is wrapped identically
    // ({@see NeoBodyStylesheet}), so a page drawn on the server and the same page drawn in the
    // browser defer to the theme the same way.
    ':where(.content-type-neo-linked-form #submit-button){display:block;width:100%;min-height:48px;margin-top:10px;padding:.5em 1.5em;border:0;border-radius:5px;background:#000;color:#fff;font-weight:700;text-transform:uppercase;cursor:pointer;}',
    ':where(.content-type-neo-linked-form .aico_label){margin:1rem 0 .1rem;font-weight:700;text-transform:uppercase;}',
    ':where(.content-type-neo-linked-form .aico_required){color:red;}',
    '.content-type-neo-testimonial .testimonial{margin:0 0 16px;padding:12px 20px;border-left:3px solid #e8702d;}',
    '.content-type-neo-testimonial .rating{display:block;color:#e8a100;}',
    '.content-type-neo-testimonial footer{margin-top:8px;font-size:14px;}',
    '.content-type-neo-testimonial .role{margin-left:8px;color:#777;}',
    '[class*="content-type-neo-"] a{text-decoration:underline;}',
    '.neo-media-gallery__masonry{column-gap:8px;}',
    '.neo-media-gallery__masonry .neo-media-gallery__item{break-inside:avoid;margin:0 0 8px;display:block;}',
    '.neo-media-gallery__carousel{position:relative;}',
    '.neo-media-gallery__carousel-track{display:flex;gap:8px;overflow-x:auto;overflow-y:hidden;scrollbar-width:none;-ms-overflow-style:none;}',
    '.neo-media-gallery__carousel-track::-webkit-scrollbar{display:none;}',
    '.neo-media-gallery__carousel .neo-media-gallery__item{flex:0 0 calc(60% - 8px);}',
    '.neo-media-gallery__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.5);color:#fff;border:0;width:36px;height:36px;border-radius:50%;cursor:pointer;z-index:1;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;}',
    '.neo-media-gallery__nav--prev{left:8px;}',
    '.neo-media-gallery__nav--next{right:8px;}',
    '.neo-media-gallery__scrubber{position:relative;height:8px;background:#e5e5e5;border-radius:4px;margin-top:16px;cursor:pointer;touch-action:none;user-select:none;overflow:hidden;}',
    '.neo-media-gallery__scrubber-thumb{position:absolute;top:0;left:0;height:100%;width:48px;background:#333;border-radius:4px;pointer-events:none;min-width:48px;display:block;}',
    '.neo-media-gallery__scrubber:hover .neo-media-gallery__scrubber-thumb{background:#111;}'
  ].join('');

  function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    try {
      var style = document.createElement('style');
      style.setAttribute('data-neo-styles', '1');
      style.appendChild(document.createTextNode(NEO_STYLES));
      (document.head || document.documentElement).appendChild(style);
    } catch (e) { /* no-op */ }
  }

  // ============================================================
  // Hydration (runs after the HTML string has been injected)
  // ============================================================

  function hydrateSlideshow(root) {
    if (root.__neoWired) return;
    root.__neoWired = true;

    var track = root.querySelector('.neo-slideshow__track');
    if (!track) return;
    var slides = track.children;
    var count = slides.length;
    if (count === 0) return;

    var dots = root.querySelectorAll('.neo-slideshow__dot');
    var prev = root.querySelector('.neo-slideshow__nav--prev');
    var next = root.querySelector('.neo-slideshow__nav--next');
    var idx = 0;
    var timer = null;

    function updateDots() {
      for (var k = 0; k < dots.length; k++) {
        if (k === idx) dots[k].classList.add('is-active');
        else dots[k].classList.remove('is-active');
      }
    }

    function goTo(i) {
      if (i < 0) i = count - 1;
      if (i >= count) i = 0;
      idx = i;
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      updateDots();
    }

    function resetAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
      if (root.getAttribute('data-autoplay') !== '1' || count < 2) return;
      var ms = parseInt(root.getAttribute('data-interval'), 10) || 5000;
      timer = setInterval(function () {
        if (!root.isConnected) { clearInterval(timer); timer = null; return; }
        goTo(idx + 1);
      }, ms);
    }

    if (prev) prev.addEventListener('click', function () { goTo(idx - 1); resetAutoplay(); });
    if (next) next.addEventListener('click', function () { goTo(idx + 1); resetAutoplay(); });
    for (var k = 0; k < dots.length; k++) {
      (function (k) {
        dots[k].addEventListener('click', function () { goTo(k); resetAutoplay(); });
      })(k);
    }

    // Track manual swipes so dots stay in sync.
    var scrollTick;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTick);
      scrollTick = setTimeout(function () {
        var w = track.clientWidth;
        if (w === 0) return;
        var i = Math.round(track.scrollLeft / w);
        if (i !== idx) { idx = i; updateDots(); }
      }, 80);
    });

    updateDots();
    resetAutoplay();
  }

  // ============================================================
  // Lightbox: click a media-gallery image → full-size singleton overlay.
  // ============================================================

  var lightbox = { el: null, imgEl: null, capEl: null, prevBtn: null, nextBtn: null, items: [], idx: 0 };

  function ensureLightbox() {
    if (lightbox.el) return;
    var el = document.createElement('div');
    el.setAttribute('data-neo-lightbox', '1');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.9);display:none;align-items:center;justify-content:center;padding:40px;';
    el.innerHTML =
      '<button type="button" data-neo-lb-close aria-label="Close" style="position:absolute;top:16px;right:16px;background:rgba(0,0,0,0.5);border:0;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:22px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;z-index:2;">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
      '</button>' +
      '<button type="button" data-neo-lb-prev aria-label="Previous" style="position:absolute;top:50%;left:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10094;</button>' +
      '<button type="button" data-neo-lb-next aria-label="Next" style="position:absolute;top:50%;right:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10095;</button>' +
      '<div data-neo-lb-stage style="max-width:90vw;max-height:90vh;display:flex;flex-direction:column;align-items:center;gap:12px;">' +
        '<img data-neo-lb-img alt="" style="max-width:100%;max-height:80vh;object-fit:contain;display:block;">' +
        '<div data-neo-lb-cap style="color:#fff;text-align:center;font-size:14px;display:none;"></div>' +
      '</div>';
    document.body.appendChild(el);

    lightbox.el = el;
    lightbox.imgEl = el.querySelector('[data-neo-lb-img]');
    lightbox.capEl = el.querySelector('[data-neo-lb-cap]');
    lightbox.prevBtn = el.querySelector('[data-neo-lb-prev]');
    lightbox.nextBtn = el.querySelector('[data-neo-lb-next]');

    el.querySelector('[data-neo-lb-close]').addEventListener('click', closeLightbox);
    lightbox.prevBtn.addEventListener('click', function () { stepLightbox(-1); });
    lightbox.nextBtn.addEventListener('click', function () { stepLightbox(1); });
    el.addEventListener('click', function (e) { if (e.target === el) closeLightbox(); });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.el || lightbox.el.style.display !== 'flex') return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
      else if (e.key === 'ArrowRight') stepLightbox(1);
    });
  }

  function showLightboxItem() {
    var item = lightbox.items[lightbox.idx];
    if (!item) return;
    lightbox.imgEl.src = item.src;
    lightbox.imgEl.alt = item.alt || '';
    if (item.caption) {
      lightbox.capEl.textContent = item.caption;
      lightbox.capEl.style.display = 'block';
    } else {
      lightbox.capEl.textContent = '';
      lightbox.capEl.style.display = 'none';
    }
    var many = lightbox.items.length > 1;
    lightbox.prevBtn.style.display = many ? 'flex' : 'none';
    lightbox.nextBtn.style.display = many ? 'flex' : 'none';
  }

  function openLightbox(items, idx) {
    if (!items || items.length === 0) return;
    ensureLightbox();
    lightbox.items = items;
    lightbox.idx = Math.max(0, Math.min(items.length - 1, idx || 0));
    showLightboxItem();
    lightbox.el.style.display = 'flex';
    lightbox.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightbox.el) return;
    lightbox.el.style.display = 'none';
    lightbox.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lightbox.items = [];
    lightbox.idx = 0;
  }

  function stepLightbox(dir) {
    if (lightbox.items.length === 0) return;
    var n = lightbox.items.length;
    lightbox.idx = (lightbox.idx + dir + n) % n;
    showLightboxItem();
  }

  function hydrateMediaGalleryLightbox(root) {
    if (root.__neoLbWired) return;
    root.__neoLbWired = true;

    var imgs = root.querySelectorAll('.neo-media-gallery__item img');
    for (var i = 0; i < imgs.length; i++) imgs[i].style.cursor = 'zoom-in';

    root.addEventListener('click', function (e) {
      var img = e.target && e.target.tagName === 'IMG' ? e.target : null;
      if (!img || !img.closest('.neo-media-gallery__item')) return;
      var all = root.querySelectorAll('.neo-media-gallery__item img');
      var items = [];
      var startIdx = 0;
      for (var k = 0; k < all.length; k++) {
        var fig = all[k].closest('.neo-media-gallery__item');
        var capNode = fig ? fig.querySelector('.neo-media-gallery__caption') : null;
        items.push({
          src: all[k].getAttribute('src') || all[k].src,
          alt: all[k].getAttribute('alt') || '',
          caption: capNode ? capNode.textContent.trim() : ''
        });
        if (all[k] === img) startIdx = k;
      }
      openLightbox(items, startIdx);
    });
  }

  function hydrateMediaGalleryCarousel(root) {
    if (root.__neoWired) return;
    root.__neoWired = true;
    var track = root.querySelector('.neo-media-gallery__carousel-track');
    if (!track) return;
    var prev = root.querySelector('.neo-media-gallery__nav--prev');
    var next = root.querySelector('.neo-media-gallery__nav--next');
    var scrubber = root.querySelector('.neo-media-gallery__scrubber');
    var thumb = root.querySelector('.neo-media-gallery__scrubber-thumb');

    function snapToItem(dir) {
      var firstItem = track.querySelector('.neo-media-gallery__item');
      var step = firstItem ? firstItem.offsetWidth + 8 : track.clientWidth;
      if (step <= 0) return;
      var current = track.scrollLeft / step;
      var nearest = Math.round(current);
      // If we're already sitting on (or sub-pixel near) an item slot, step away from it.
      // Otherwise floor/ceil to the next slot in the requested direction.
      var targetIdx = (Math.abs(current - nearest) < 0.1)
        ? nearest + dir
        : (dir > 0 ? Math.ceil(current) : Math.floor(current));
      if (targetIdx < 0) targetIdx = 0;
      track.scrollTo({ left: targetIdx * step, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { snapToItem(-1); });
    if (next) next.addEventListener('click', function () { snapToItem(1); });

    if (!scrubber || !thumb) return;

    function updateThumb() {
      var sw = track.scrollWidth;
      var cw = track.clientWidth;
      if (sw <= cw) { thumb.style.width = '100%'; thumb.style.left = '0'; return; }
      var widthPct = (cw / sw) * 100;
      var leftPct = (track.scrollLeft / (sw - cw)) * (100 - widthPct);
      thumb.style.width = widthPct + '%';
      thumb.style.left = leftPct + '%';
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { updateThumb(); ticking = false; });
    });
    window.addEventListener('resize', updateThumb);

    function pointerToScrollLeft(clientX) {
      var rect = scrubber.getBoundingClientRect();
      var ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      var sw = track.scrollWidth;
      var cw = track.clientWidth;
      return ratio * (sw - cw);
    }

    var dragging = false;
    scrubber.addEventListener('pointerdown', function (e) {
      dragging = true;
      scrubber.setPointerCapture(e.pointerId);
      track.scrollLeft = pointerToScrollLeft(e.clientX);
    });
    scrubber.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      track.scrollLeft = pointerToScrollLeft(e.clientX);
    });
    scrubber.addEventListener('pointerup', function (e) {
      dragging = false;
      if (scrubber.hasPointerCapture && scrubber.hasPointerCapture(e.pointerId)) {
        scrubber.releasePointerCapture(e.pointerId);
      }
    });
    scrubber.addEventListener('pointercancel', function () { dragging = false; });

    // Defer to after layout (initial scrollWidth/clientWidth can both be 0),
    // and re-run when the track resizes (lazy images, viewport changes).
    requestAnimationFrame(updateThumb);
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(updateThumb);
      ro.observe(track);
    }
  }

  function hydrate(root) {
    injectStyles();
    var scope = root && root.querySelectorAll ? root : document;
    var slideshows = scope.querySelectorAll('[data-neo-slideshow="1"]');
    for (var i = 0; i < slideshows.length; i++) hydrateSlideshow(slideshows[i]);
    var carousels = scope.querySelectorAll('[data-neo-mg-carousel="1"]');
    for (var j = 0; j < carousels.length; j++) hydrateMediaGalleryCarousel(carousels[j]);
    var galleries = scope.querySelectorAll('.content-type-neo-media-gallery');
    for (var k = 0; k < galleries.length; k++) hydrateMediaGalleryLightbox(galleries[k]);
  }

  // ============================================================
  // Public API
  // ============================================================

  window.NewsNeo = {
    hasContent: hasNeoContent,
    render: render,
    hydrate: hydrate,
    registerType: function (type, fn) {
      if (typeof type === 'string' && typeof fn === 'function') {
        renderers[type] = fn;
      }
    },
    // {type: count} of the elements the last render() could not draw.
    lastUnrendered: lastUnrendered,
    _renderers: renderers
  };

})(window);

/**
 * Bootstrap (AICO-theme addition).
 *
 * Reads the inline page-data JSON, renders it into the page mount and hydrates.
 * Inert when there is no mount, no data, or no Neo content — so an empty page
 * (or a theme that only sometimes carries Neo content) renders nothing instead
 * of erroring.
 */
(function (window, document) {
  'use strict';

  function boot() {
    var mount = document.getElementById('aico-content');
    var dataEl = document.getElementById('aico-page-data');
    if (!mount || !dataEl || !window.NewsNeo) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
    } catch (e) {
      if (window.console && console.warn) console.warn('content-builder-neo: bad page data JSON', e);
      return;
    }

    if (!window.NewsNeo.hasContent(data)) return;

    mount.innerHTML = window.NewsNeo.render(data);
    window.NewsNeo.hydrate(mount);

    // Stamp what could not be drawn onto the mount, so a missing block is one
    // attribute lookup away for a DOM check, not a console session.
    var unrendered = window.NewsNeo.lastUnrendered ? window.NewsNeo.lastUnrendered() : {};
    var missing = Object.keys(unrendered).sort();
    if (missing.length) {
      mount.setAttribute('data-neo-unrendered', missing.map(function (t) { return t + ':' + unrendered[t]; }).join(' '));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(window, document);
